import React from 'react';
import { Pencil, Trash2, Eye } from 'lucide-react';
import FormSection from '../../../components/common/FormSection';
import Button from '../../../components/common/Button';
import { PagesDTO } from '../../../services/apiClient';
import { renderBooleanBadge } from '../../../utils/displayUtils';
import { buildFullUrl, buildPagePath } from '../../../modules/infoHub';
import { BASE_URL } from '../../../config/config';

interface PageDetailViewProps {
  page: PagesDTO;
  parentTitle?: string;
  onEdit: () => void;
  onDelete: () => void;
}

const ReadonlyField: React.FC<{
  label: string;
  value?: React.ReactNode;
}> = ({ label, value }) => (
  <div className="mb-4">
    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
      {label}
    </div>
    <div className="text-gray-800 dark:text-gray-100 break-words">
      {value === undefined || value === null || value === '' ? '—' : value}
    </div>
  </div>
);

const PageDetailView: React.FC<PageDetailViewProps> = ({
  page,
  parentTitle,
  onEdit,
  onDelete,
}) => {
  const handlePreview = () => {
    window.open(buildPagePath(page.slugFull), '_blank', 'noopener,noreferrer');
  };

  const actions = (
    <div className="flex items-center gap-2">
      <Button variant="secondary" onClick={handlePreview} icon={Eye}>
        Preview
      </Button>
      <Button onClick={onEdit} icon={Pencil}>
        Edit
      </Button>
      <Button variant="danger" onClick={onDelete} icon={Trash2}>
        Delete
      </Button>
    </div>
  );

  return (
    <>
      <FormSection title="Basic Information" headerActions={actions}>
        <ReadonlyField label="Title" value={page.title} />
        <ReadonlyField label="Parent Page" value={parentTitle} />
        <ReadonlyField label="Title URL" value={page.slug} />
        <ReadonlyField label="Path URL" value={buildFullUrl(BASE_URL, page.slugFull)} />
        <ReadonlyField label="Page Position" value={page.position} />
        <ReadonlyField label="Description" value={page.description} />
      </FormSection>

      <FormSection title="Settings">
        <ReadonlyField
          label="Visible in navigation"
          value={renderBooleanBadge(page.isVisible ?? false, 'Visible', 'Hidden')}
        />
        <ReadonlyField
          label="Restricted"
          value={page.isRestricted ? 'Restricted' : 'Visible to all'}
        />
        <ReadonlyField
          label="Active"
          value={renderBooleanBadge(page.isActive ?? false, 'Yes', 'No')}
        />
      </FormSection>
    </>
  );
};

export default PageDetailView;
